import React, { createContext, useContext, useState, useEffect } from 'react';

const WatchlistContext = createContext();

export const useWatchlist = () => {
  const context = useContext(WatchlistContext);
  if (!context) {
    throw new Error('useWatchlist must be used within a WatchlistProvider');
  }
  return context;
};

export const WatchlistProvider = ({ children }) => {
  const [watchlists, setWatchlists] = useState(() => {
    const saved = localStorage.getItem('stocks4u_watchlists');
    if (saved) {
      return JSON.parse(saved);
    }
    return [
      {
        id: 'default',
        name: 'My Watchlist',
        stocks: [],
        createdAt: new Date().toISOString()
      }
    ];
  });

  const [activeWatchlistId, setActiveWatchlistId] = useState('default');

  useEffect(() => {
    localStorage.setItem('stocks4u_watchlists', JSON.stringify(watchlists));
    
    // Validate activeWatchlistId exists in watchlists
    const activeExists = watchlists.some(wl => wl.id === activeWatchlistId);
    if (!activeExists && watchlists.length > 0) {
      setActiveWatchlistId(watchlists[0].id);
    }
  }, [watchlists, activeWatchlistId]);

  useEffect(() => {
    localStorage.setItem('stocks4u_active_watchlist', activeWatchlistId);
  }, [activeWatchlistId]);

  const createWatchlist = (name) => {
    const newWatchlist = {
      id: `watchlist_${Date.now()}`,
      name: name || `Watchlist ${watchlists.length + 1}`,
      stocks: [],
      createdAt: new Date().toISOString()
    };
    setWatchlists(prev => [...prev, newWatchlist]);
    return newWatchlist.id;
  };

  const renameWatchlist = (id, newName) => {
    setWatchlists(prev => 
      prev.map(wl => wl.id === id ? { ...wl, name: newName } : wl)
    );
  };

  const deleteWatchlist = (id) => {
    if (watchlists.length === 1) return;
    const updatedWatchlists = watchlists.filter(wl => wl.id !== id);
    setWatchlists(updatedWatchlists);
    if (activeWatchlistId === id) {
      setActiveWatchlistId(updatedWatchlists[0].id);
    }
  };

  const addToWatchlist = (stock, watchlistId = activeWatchlistId) => {
    setWatchlists(prev => 
      prev.map(wl => {
        if (wl.id === watchlistId) {
          const exists = wl.stocks.find(item => item.symbol === stock.symbol);
          if (exists) return wl;
          
          return {
            ...wl,
            stocks: [...wl.stocks, {
              ...stock,
              addedAt: new Date().toISOString()
            }]
          };
        }
        return wl;
      })
    );
  };

  const removeFromWatchlist = (symbol, watchlistId = activeWatchlistId) => {
    setWatchlists(prev => 
      prev.map(wl => {
        if (wl.id === watchlistId) {
          return {
            ...wl,
            stocks: wl.stocks.filter(item => item.symbol !== symbol)
          };
        }
        return wl;
      })
    );
  };

  const isInWatchlist = (symbol) => {
    return watchlists.some(wl => 
      wl.stocks.some(item => item.symbol === symbol)
    );
  };

  const isInSpecificWatchlist = (symbol, watchlistId) => {
    const watchlist = watchlists.find(wl => wl.id === watchlistId);
    return watchlist ? watchlist.stocks.some(item => item.symbol === symbol) : false;
  };

  const getWatchlistsForStock = (symbol) => {
    return watchlists.filter(wl => 
      wl.stocks.some(item => item.symbol === symbol)
    );
  };

  const clearWatchlist = (watchlistId = activeWatchlistId) => {
    setWatchlists(prev => 
      prev.map(wl => {
        if (wl.id === watchlistId) {
          return { ...wl, stocks: [] };
        }
        return wl;
      })
    );
  };

  const activeWatchlist = watchlists.find(wl => wl.id === activeWatchlistId) || watchlists[0] || { id: 'default', name: 'My Watchlist', stocks: [], createdAt: new Date().toISOString() };

  return (
    <WatchlistContext.Provider
      value={{
        watchlists,
        activeWatchlist,
        activeWatchlistId,
        setActiveWatchlistId,
        createWatchlist,
        renameWatchlist,
        deleteWatchlist,
        addToWatchlist,
        removeFromWatchlist,
        isInWatchlist,
        isInSpecificWatchlist,
        getWatchlistsForStock,
        clearWatchlist,
        watchlist: activeWatchlist?.stocks || []
      }}
    >
      {children}
    </WatchlistContext.Provider>
  );
};
